// ═══════════════════════════════════════════════════════════════════════════
// Repartir cartera y poner comisiones
//
// Es la pantalla que faltaba para que todo lo de la base sirva de algo: las
// funciones existían y no había por dónde llamarlas.
//
// DOS DECISIONES DE LA PANTALLA:
//
//   · Asignar y poner tasa son DOS BOTONES, no uno. Podrían ir juntos, y esa
//     comodidad es justamente lo que se quitó en la migración 0017: una tasa
//     que se aplica de pasada convierte un descuido en dinero. Aquí hay que
//     escribir el número y darle a otro botón.
//
//   · Se muestra a quién se le está QUITANDO la cuenta antes de confirmar.
//     Tomar una cuenta de la casa y quitársela a un compañero son cosas
//     distintas, y la segunda se avisa.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { clientesParaCartera, asignarCartera, fijarComision } from "../datos/vendedores.js";
import { Card, Label } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";

export function CarteraPanel({ tenantId, gente, onCerrar, onCambio }) {
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todos");   // todos | casa | asignados | sin_tasa
  const [sel, setSel] = useState(() => new Set());
  const [destino, setDestino] = useState("");
  const [pct, setPct] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const activos = useMemo(() => (gente || []).filter(g => g.is_active), [gente]);
  const nombreDe = useCallback(
    (mid) => activos.find(g => g.membership_id === mid)?.nombre
             || activos.find(g => g.membership_id === mid)?.email
             || "otro vendedor",
    [activos]);

  const recargar = useCallback(async () => {
    setCargando(true); setError("");
    try { setClientes(await clientesParaCartera(tenantId)); }
    catch (e) { setError(e.message); }
    setCargando(false);
  }, [tenantId]);

  useEffect(() => { recargar(); }, [recargar]);

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return clientes.filter(c => {
      if (q && !(`${c.name} ${c.city || ""} ${c.rfc || ""}`.toLowerCase().includes(q))) return false;
      if (filtro === "casa")      return !c.assigned_agent_id;
      if (filtro === "asignados") return !!c.assigned_agent_id;
      if (filtro === "sin_tasa")  return c.assigned_agent_id && c.commission_pct == null;
      return true;
    });
  }, [clientes, busca, filtro]);

  const alternar = (id) => setSel(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const todos = () => setSel(new Set(visibles.map(c => c.legacy_id)));
  const ninguno = () => setSel(new Set());

  // A quién se le quitarían cuentas con esta selección.
  const quitandoA = useMemo(() => {
    const m = new Map();
    for (const c of clientes) {
      if (!sel.has(c.legacy_id)) continue;
      if (!c.assigned_agent_id || c.assigned_agent_id === destino) continue;
      m.set(c.assigned_agent_id, (m.get(c.assigned_agent_id) || 0) + 1);
    }
    return [...m.entries()];
  }, [clientes, sel, destino]);

  const asignar = async () => {
    if (!sel.size) return;
    const ids = [...sel];
    const aCasa = destino === "";
    const etq = aCasa ? "la casa (sin vendedor)" : nombreDe(destino);

    let aviso = `${ids.length} cliente(s) → ${etq}.`;
    if (quitandoA.length)
      aviso += `\n\n⚠️ Se le quitan a: ` +
        quitandoA.map(([m, n]) => `${nombreDe(m)} (${n})`).join(", ");
    aviso += aCasa
      ? `\n\nAl volver a la casa se les quita la comisión.`
      : `\n\nQuedan SIN comisión hasta que le pongas tasa. No se hereda ninguna.`;
    if (!confirm(aviso)) return;

    setOcupado(true); setError("");
    try {
      const motivo = prompt("Motivo (queda en el historial de cartera):") || null;
      await asignarCartera(tenantId, ids, aCasa ? null : destino, motivo);
      await recargar(); onCambio?.(); ninguno();
    } catch (e) { setError(e.message); }
    setOcupado(false);
  };

  const aplicarTasa = async () => {
    if (!sel.size) return;
    const n = Number(pct);
    if (!Number.isFinite(n) || n < 0 || n > 100) { setError("La comisión va de 0 a 100."); return; }
    const ids = [...sel];
    const sinVendedor = clientes.filter(c => sel.has(c.legacy_id) && !c.assigned_agent_id).length;

    let aviso = `Comisión de ${n}% a ${ids.length} cliente(s).\n\n` +
      `Se aplica sobre el total CON impuestos de sus remisiones, y cubre también el reparto.`;
    if (sinVendedor)
      aviso += `\n\n⚠️ ${sinVendedor} no tiene vendedor: a esos no se les aplica nada.`;
    if (!confirm(aviso)) return;

    setOcupado(true); setError("");
    try {
      const motivo = prompt("Motivo (queda en el historial):") || null;
      const r = await fijarComision(tenantId, ids, n, motivo);
      await recargar(); onCambio?.(); ninguno(); setPct("");
      if (r?.omitidos_sin_vendedor > 0)
        alert(`Listo. Se omitieron ${r.omitidos_sin_vendedor} sin vendedor asignado.`);
    } catch (e) { setError(e.message); }
    setOcupado(false);
  };

  const cuenta = {
    casa: clientes.filter(c => !c.assigned_agent_id).length,
    sinTasa: clientes.filter(c => c.assigned_agent_id && c.commission_pct == null).length,
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 1000,
                  display: "flex", alignItems: "flex-start", justifyContent: "center",
                  overflow: "auto", padding: 16 }}
      onClick={onCerrar}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 12,
                 maxWidth: 900, width: "100%", marginTop: 16, padding: 20 }}>

        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, color: "#c9a96e" }}>
            Repartir cartera
          </div>
          <button onClick={onCerrar} style={{ ...btnSecondary, marginLeft: "auto",
            padding: "4px 12px", fontSize: 12 }}>Cerrar</button>
        </div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 16 }}>
          {clientes.length} clientes · {cuenta.casa} de la casa
          {cuenta.sinTasa > 0 && <> · <span style={{ color: "#c9a96e" }}>{cuenta.sinTasa} sin tasa</span></>}
        </div>

        {error && (
          <div style={{ padding: "8px 12px", background: "#1f0d0d", border: "1px solid #3a1a1a",
                        borderRadius: 6, fontSize: 12, color: "#f87171", marginBottom: 12 }}>
            {error}
          </div>
        )}

        {/* ── Filtros ────────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <input value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar por nombre, ciudad o RFC…"
            style={{ ...inputStyle, flex: 1, minWidth: 200 }} />
          <select value={filtro} onChange={e => setFiltro(e.target.value)}
            style={{ ...selectStyle, width: "auto" }}>
            <option value="todos">Todos</option>
            <option value="casa">Sin vendedor</option>
            <option value="asignados">Asignados</option>
            <option value="sin_tasa">Asignados sin tasa</option>
          </select>
        </div>

        {/* ── Acciones sobre lo seleccionado ─────────────────────────── */}
        <div style={{ padding: 12, background: sel.size ? "#12140d" : "#0a0a0a",
                      border: `1px solid ${sel.size ? "#2a3a1a" : "#1a1a1a"}`,
                      borderRadius: 8, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: sel.size ? "#c9d8a0" : "#555", marginBottom: 10 }}>
            {sel.size ? `${sel.size} seleccionado(s)` : "Selecciona clientes para asignarlos o ponerles tasa"}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ minWidth: 180 }}>
              <Label>Asignar a</Label>
              <select value={destino} onChange={e => setDestino(e.target.value)} style={selectStyle}>
                <option value="">La casa (sin vendedor)</option>
                {activos.map(g => (
                  <option key={g.membership_id} value={g.membership_id}>
                    {g.nombre || g.email}
                  </option>
                ))}
              </select>
            </div>
            <button onClick={asignar} disabled={!sel.size || ocupado}
              style={{ ...btnSecondary, padding: "9px 16px", fontSize: 12,
                       opacity: (!sel.size || ocupado) ? 0.35 : 1 }}>
              Asignar
            </button>

            <div style={{ width: 1, alignSelf: "stretch", background: "#222", margin: "0 4px" }} />

            <div style={{ width: 110 }}>
              <Label>Comisión %</Label>
              <input type="number" min="0" max="100" step="0.5" value={pct}
                onChange={e => setPct(e.target.value)} placeholder="10"
                style={inputStyle} />
            </div>
            <button onClick={aplicarTasa} disabled={!sel.size || pct === "" || ocupado}
              style={{ ...btnPrimary, padding: "9px 16px", fontSize: 12,
                       opacity: (!sel.size || pct === "" || ocupado) ? 0.35 : 1 }}>
              Fijar comisión
            </button>
          </div>

          {/* Son dos acciones separadas a propósito: ver 0017. */}
          <div style={{ fontSize: 10, color: "#556", marginTop: 8, lineHeight: 1.6 }}>
            Asignar NO pone comisión. Se escribe el porcentaje y se aplica aparte,
            porque cubre también el reparto y depende de dónde está cada cuenta.
          </div>
        </div>

        {/* ── La lista ───────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 10, marginBottom: 6, fontSize: 11 }}>
          <button onClick={todos} style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11 }}>
            Todos los visibles ({visibles.length})
          </button>
          <button onClick={ninguno} style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11 }}>
            Ninguno
          </button>
        </div>

        <div style={{ maxHeight: "45vh", overflowY: "auto", border: "1px solid #1a1a1a",
                      borderRadius: 6 }}>
          {cargando ? (
            <div style={{ padding: 20, color: "#555", fontSize: 13 }}>Cargando…</div>
          ) : visibles.length === 0 ? (
            <div style={{ padding: 20, color: "#555", fontSize: 13 }}>Nada que mostrar.</div>
          ) : visibles.map(c => {
            const marcado = sel.has(c.legacy_id);
            return (
              <div key={c.legacy_id} onClick={() => alternar(c.legacy_id)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                         borderBottom: "1px solid #141414", cursor: "pointer",
                         background: marcado ? "#161a10" : "transparent" }}>
                <input type="checkbox" readOnly checked={marcado} style={{ pointerEvents: "none" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "#e8e0d4", overflow: "hidden",
                                textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.name}
                  </div>
                  <div style={{ fontSize: 10, color: "#666" }}>
                    {[c.city, c.state].filter(Boolean).join(", ")}
                    {c.rfc ? ` · ${c.rfc}` : " · sin RFC"}
                  </div>
                </div>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                               background: "#141414",
                               color: c.assigned_agent_id ? "#a0a0c9" : "#555" }}>
                  {c.assigned_agent_id ? nombreDe(c.assigned_agent_id) : "la casa"}
                </span>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, minWidth: 52,
                               textAlign: "center",
                               background: c.commission_pct != null ? "#12140d" : "#1f1a0d",
                               color: c.commission_pct != null ? "#a8d060"
                                    : c.assigned_agent_id ? "#c9a96e" : "#444" }}>
                  {c.commission_pct != null ? `${Number(c.commission_pct)}%`
                    : c.assigned_agent_id ? "sin tasa" : "—"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
